import { useState, useEffect, useCallback, useRef } from "react";

import {
  sessionStorage,
  type StoredSession,
} from "@/lib/session-storage";

interface UseSessionPersistenceReturn {
  /** The stored session token, if available */
  sessionToken: string | null;
  /** The stored response ID, if available */
  responseId: string | null;
  /** Whether session restoration is in progress */
  isRestoringSession: boolean;
  /** Save a new session to storage */
  saveSession: (responseId: string, sessionToken: string) => void;
  /** Clear the stored session */
  clearSession: () => void;
}

/**
 * Hook for managing session persistence in localStorage.
 * Automatically checks for and restores existing sessions on mount.
 */
export function useSessionPersistence(
  projectId: string,
  formId: string,
  version: number,
): UseSessionPersistenceReturn {
  const [sessionToken, setSessionToken] = useState<string | null>(null);
  const [responseId, setResponseId] = useState<string | null>(null);
  const [isRestoringSession, setIsRestoringSession] = useState(true);
  const initializedRef = useRef(false);

  // Check for existing session on mount
  useEffect(() => {
    if (initializedRef.current) return;
    initializedRef.current = true;

    const stored = sessionStorage.get(projectId, formId, version);

    if (stored) {
      setSessionToken(stored.sessionToken);
      setResponseId(stored.responseId);
    }

    setIsRestoringSession(false);
  }, [projectId, formId, version]);

  const saveSession = useCallback(
    (newResponseId: string, newSessionToken: string) => {
      const session: StoredSession = {
        responseId: newResponseId,
        sessionToken: newSessionToken,
        projectId,
        formId,
        version,
        createdAt: new Date().toISOString(),
      };

      sessionStorage.set(session);
      setSessionToken(newSessionToken);
      setResponseId(newResponseId);
    },
    [projectId, formId, version],
  );

  const clearSessionCallback = useCallback(() => {
    sessionStorage.clear(projectId, formId, version);
    setSessionToken(null);
    setResponseId(null);
  }, [projectId, formId, version]);

  return {
    sessionToken,
    responseId,
    isRestoringSession,
    saveSession,
    clearSession: clearSessionCallback,
  };
}
